import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import RouteHead from '../../components/RouteHead/RouteHead';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { WEB_APP_URL } from '../../utils/webApp';
import screenWorldMap from '../../assets/screen-world-map.webp';
import countriesData from '../../data/countries.json';
import countryBounds from '../../data/countryBounds';
import MAP_FAQS from '../../data/mapFaqs.json';
import './VisitedMapPage.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const STORAGE_KEY = 'voia.visitedCountries';
const MAP_STYLE = 'mapbox://styles/talb27/cmilydp6m00rg01qt87xdegcz';
const SITE_URL = 'https://voiatravel.com';

const APP_STORE_URL = 'https://apps.apple.com/pt/app/voia-travel/id6747190886?l=en-GB';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.voia.travel.mobile';

const COUNTRIES = countriesData.countries;
const TOTAL_COUNTRIES = COUNTRIES.length;
const VALID_CODES = new Set(COUNTRIES.map((c) => c.code));

const CONTINENT_ORDER = ['Europe', 'Asia', 'Africa', 'North America', 'South America', 'Oceania'];

/** Shortened purely for the tab strip, so all six continents fit in two rows. */
const CONTINENT_TAB_LABELS = {
  'North America': 'N. America',
  'South America': 'S. America',
};

/**
 * Centre of a country's bounding box.
 * Boxes that cross the antimeridian are stored with west > east (Fiji is
 * [177, …, -179, …]); averaging those raw gives a point in the Atlantic, so the
 * eastern edge is unwrapped by 360° first and the result re-wrapped.
 */
const centroidOf = (bounds) => {
  const [west, south, east, north] = bounds;
  let lng = west > east ? (west + east + 360) / 2 : (west + east) / 2;
  if (lng > 180) lng -= 360;
  return [lng, (south + north) / 2];
};

const byName = (a, b) => a.name.localeCompare(b.name);

const normalise = (value) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const sanitiseCodes = (codes) => codes.filter((code) => VALID_CODES.has(code));

/** URL wins over localStorage, so a shared link always shows the sender's map. */
const readInitialCodes = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('c');
    if (fromUrl) {
      const parsed = sanitiseCodes(fromUrl.split(',').map((c) => c.trim().toUpperCase()));
      if (parsed.length) return parsed;
    }
  } catch {
    /* malformed query string — fall through to storage */
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? sanitiseCodes(parsed.filter((c) => typeof c === 'string')) : [];
  } catch {
    return [];
  }
};


const VisitedMapPage = () => {
  const [selected, setSelected] = useState(() => new Set(readInitialCodes()));
  const [query, setQuery] = useState('');
  const [activeContinent, setActiveContinent] = useState(CONTINENT_ORDER[0]);
  // Tracked in state, not just the ref: the failure notice has to re-render
  // once the globe is ready, which a ref assignment alone would not trigger.
  const [mapReady, setMapReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const [mapAttempt, setMapAttempt] = useState(0);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map());
  const lastAddedRef = useRef(null);

  const count = selected.size;
  const percent = Math.round((count / TOTAL_COUNTRIES) * 100);
  const hasSelection = count > 0;

  const countriesByCode = useMemo(() => {
    const map = new Map();
    COUNTRIES.forEach((country) => map.set(country.code, country));
    return map;
  }, []);

  // Every continent, in a stable order — the tab strip is built from this.
  const continents = useMemo(() => {
    const buckets = new Map();
    COUNTRIES.forEach((country) => {
      const key = country.continent || 'Other';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(country);
    });
    const ordered = [
      ...CONTINENT_ORDER.filter((k) => buckets.has(k)),
      ...[...buckets.keys()].filter((k) => !CONTINENT_ORDER.includes(k)),
    ];
    return ordered.map((name) => ({ name, countries: buckets.get(name).sort(byName) }));
  }, []);

  const searching = query.trim().length > 0;

  // Searching cuts across continents, so it replaces the tabs with grouped
  // results; otherwise only the open continent is listed.
  const groups = useMemo(() => {
    if (!searching) {
      const active = continents.find((c) => c.name === activeContinent) || continents[0];
      return active ? [{ continent: active.name, countries: active.countries }] : [];
    }

    const needle = normalise(query.trim());
    return continents
      .map(({ name, countries }) => ({
        continent: name,
        countries: countries.filter((c) => normalise(c.name).includes(needle)),
      }))
      .filter(({ countries }) => countries.length > 0);
  }, [query, searching, continents, activeContinent]);

  const selectedPerContinent = useMemo(() => {
    const counts = new Map();
    selected.forEach((code) => {
      const country = countriesByCode.get(code);
      if (!country) return;
      const key = country.continent || 'Other';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [selected, countriesByCode]);


  const toggleCountry = useCallback((code) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(code)) {
        next.delete(code);
        lastAddedRef.current = null;
      } else {
        next.add(code);
        lastAddedRef.current = code;
      }
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    lastAddedRef.current = null;
    setSelected(new Set());
  }, []);

  // Persist locally and keep the address bar shareable.
  useEffect(() => {
    const codes = [...selected];
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
    } catch {
      /* private mode — the session still works */
    }

    const url = new URL(window.location.href);
    if (codes.length) url.searchParams.set('c', codes.join(','));
    else url.searchParams.delete('c');
    window.history.replaceState(null, '', url);
  }, [selected]);

  // The globe is only created once there is something to show, so visitors who
  // never engage cost nothing in Mapbox map loads.
  useEffect(() => {
    if (!hasSelection || mapRef.current || !mapContainerRef.current) return;
    if (!mapboxgl.accessToken) return;

    setMapFailed(false);

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      projection: 'globe',
      // Sized to the space it has rather than a constant. A Mapbox globe's
      // on-screen diameter is (512 * 2^zoom) / PI and does not depend on the
      // canvas, so a fixed 1.5 drew the same 461px sphere whether it had 840px
      // or 1080px to fill, and the gap to the picker grew with the window.
      zoom: (() => {
        if (window.innerWidth <= 768) return 1.0;
        const box = mapContainerRef.current.getBoundingClientRect();
        if (!box.width || !box.height) return 1.5;
        const diameter = Math.min(box.width, box.height) * 0.92;
        return Math.max(1.0, Math.min(2.4, Math.log2((diameter * Math.PI) / 512)));
      })(),
      center: [15, 25],
      pitchWithRotate: false,
      // Placed manually below: the default bottom-right corner sits behind the
      // picker panel, and Mapbox requires attribution to stay visible.
      attributionControl: false,
    });

    map.on('style.load', () => {
      map.setFog({
        color: '#B8E6FF',
        'high-color': '#5ab8e6',
        'horizon-blend': 0.04,
        'space-color': '#B8E6FF',
        'star-intensity': 0.6,
      });
    });

    // Mapbox can hand back a 5xx on the style request and never finish loading,
    // which would otherwise leave a silent blank globe and a dead export button.
    // Generous: Mapbox can take well over 10s under load, and the notice hides
    // itself again if the globe does eventually arrive.
    const failTimer = window.setTimeout(() => setMapFailed(true), 22000);

    map.on('load', () => {
      window.clearTimeout(failTimer);
      map.resize();
      setMapReady(true);
      setMapFailed(false);
    });

    map.scrollZoom.disable();
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-left');
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');

    mapRef.current = map;

    return () => window.clearTimeout(failTimer);
  }, [hasSelection, mapAttempt]);

  const retryMap = useCallback(() => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current.clear();
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    setMapReady(false);
    setMapFailed(false);
    setMapAttempt((attempt) => attempt + 1);
  }, []);

  // One flag marker per selected country.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const markers = markersRef.current;

    markers.forEach((marker, code) => {
      if (!selected.has(code)) {
        marker.remove();
        markers.delete(code);
      }
    });

    selected.forEach((code) => {
      if (markers.has(code)) return;
      const bounds = countryBounds[code.toLowerCase()];
      const country = countriesByCode.get(code);
      if (!bounds || !country) return;

      const el = document.createElement('div');
      el.className = 'vmFlagPin';
      el.title = country.name;

      const img = document.createElement('img');
      // Flags are served from our own origin, so they never taint the export
      // canvas and need no crossOrigin attribute.
      img.src = `/${country.flag_rounded}`;
      img.alt = '';
      el.appendChild(img);

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(centroidOf(bounds))
        .addTo(map);
      markers.set(code, marker);
    });

    const justAdded = lastAddedRef.current;
    if (justAdded && selected.has(justAdded)) {
      const bounds = countryBounds[justAdded.toLowerCase()];
      if (bounds) map.easeTo({ center: centroidOf(bounds), duration: 900 });
      lastAddedRef.current = null;
    }
    // mapAttempt matters: a retry builds a fresh map, and without it the pins
    // would never be re-attached to the new instance.
  }, [selected, countriesByCode, mapAttempt]);

  useEffect(
    () => () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current.clear();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setMapReady(false);
    },
    []
  );



  const mapUnavailable = !mapboxgl.accessToken;
  const shareUrl = `${SITE_URL}/visited-countries-map`;

  return (
    <div className="vmPage">
      <RouteHead path="/visited-countries-map" />

      {/* Holds the first screen: navbar plus globe fill the viewport exactly,
          and the sections below are reached by scrolling. Before this wrapper
          .vmPage was itself locked to 100dvh with overflow hidden, which left
          no room for anything after the map. */}
      <div className="vmViewport">
        <Navbar variant="blue" />

        <main className="vmStage">
        {/* Left column: the globe owns its own space, so it centres in the
            space you can actually see rather than behind the picker. */}
        <div className="vmGlobeCol">
          {/* Banner sits above the globe in normal flow, title left and running
              score right; the globe takes whatever height is left. */}
          <div className="vmHud">
            <h1 className="vmTitle">Map the countries you&apos;ve visited</h1>
            <div className="vmScore">
              <div className="vmScoreItem">
                <span className="vmScoreValue">{percent}%</span>
                <span className="vmScoreLabel">World</span>
              </div>
              <span className="vmScoreDivider" aria-hidden="true" />
              <div className="vmScoreItem">
                <span className="vmScoreValue">{count}</span>
                <span className="vmScoreLabel">{count === 1 ? 'Country' : 'Countries'}</span>
              </div>
            </div>
          </div>

          <div className="vmGlobeArea">
            <div ref={mapContainerRef} className="vmGlobeCanvas" />

            {(!hasSelection || mapUnavailable) && (
              <div className="vmGlobeEmpty">
                <span className="vmGlobeEmptyIcon" role="img" aria-label="">
                  🌍
                </span>
                <p className="vmGlobeEmptyText">
                  {mapUnavailable
                    ? 'The globe is unavailable right now, but you can still build your list.'
                    : 'Pick your first country and your world starts filling up.'}
                </p>
              </div>
            )}

            {hasSelection && !mapUnavailable && mapFailed && !mapReady && (
              <div className="vmGlobeEmpty vmGlobeError">
                <span className="vmGlobeEmptyIcon" role="img" aria-label="">
                  🌐
                </span>
                <p className="vmGlobeEmptyText">
                  The globe didn&apos;t load — that&apos;s usually a temporary hiccup. Your countries
                  are safe.
                </p>
                <button type="button" className="vmRetryButton" onClick={retryMap}>
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right column: picker */}
        <aside className="vmPanel" aria-label="Choose the countries you have visited">
          <div className="vmPanelBody">
            <div className="vmPickerBar">
              <input
                type="search"
                className="vmSearch"
                placeholder="Search countries…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Search countries"
              />
              {hasSelection && (
                <button type="button" className="vmClear" onClick={clearAll}>
                  Clear
                </button>
              )}
            </div>

            {/* One continent at a time keeps the list a predictable length
                instead of a single 195-country scroll. */}
            {!searching && (
              <div className="vmTabs" role="tablist" aria-label="Continents">
                {continents.map(({ name, countries }) => {
                  const picked = selectedPerContinent.get(name) || 0;
                  const isActive = name === activeContinent;
                  return (
                    <button
                      key={name}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`vmTab${isActive ? ' vmTabActive' : ''}`}
                      onClick={() => setActiveContinent(name)}
                    >
                      <span className="vmTabName">{CONTINENT_TAB_LABELS[name] || name}</span>
                      <span className={`vmTabCount${picked ? ' vmTabCountOn' : ''}`}>
                        {picked}/{countries.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="vmPanelScroll">
              {groups.length === 0 ? (
                <p className="vmNoResults">No countries match “{query}”.</p>
              ) : (
                groups.map(({ continent, countries }) => (
                  <div key={continent} className="vmGroup">
                    {searching && <h2 className="vmGroupTitle">{continent}</h2>}
                    <ul className="vmChipList">
                      {countries.map((country) => {
                        const isOn = selected.has(country.code);
                        return (
                          <li key={country.code}>
                            <button
                              type="button"
                              className={`vmChip${isOn ? ' vmChipOn' : ''}`}
                              onClick={() => toggleCountry(country.code)}
                              aria-pressed={isOn}
                            >
                              <img
                                src={`/${country.flag_rounded}`}
                                alt=""
                                className="vmChipFlag"
                                loading="lazy"
                                width="22"
                                height="22"
                              />
                              <span className="vmChipName">{country.name}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              )}
            </div>

          </div>

          {/* Desktop: once they've seen their map, sign-up is one click away in the web app,
              without leaving the browser. The picks don't carry over: onboarding has its own
              countries step. Phones get the store pitch below. */}
          {hasSelection && (
            <div className="vmPanelSave onlyDesktop" data-track-location="map_builder_panel">
              <a href={WEB_APP_URL} className="vmSaveButton">
                Create a free account
              </a>
              <span className="vmSaveHint">Free with Apple or Google</span>
            </div>
          )}
        </aside>

        </main>
      </div>

      {/* The map above lives in localStorage and a URL. This is the pitch for
          moving it somewhere permanent, so it leads with what the app adds
          rather than repeating what the visitor just did. */}
      <section className="vmKeep">
        <div className="vmKeepInner">
          <div className="vmKeepCopy">
            <span className="vmKeepEyebrow">Keep your map</span>
            <h2 className="vmKeepTitle">
              {hasSelection ? (
                <>
                  Take your {count} {count === 1 ? 'country' : 'countries'}{' '}
                  <span className="vmKeepTitleAccent">further</span>
                </>
              ) : (
                <>
                  Keep your map, keep your{' '}
                  <span className="vmKeepTitleAccent">travel journey</span>
                </>
              )}
            </h2>
            <p className="vmKeepBody">
              This map lives in your browser. On VOIA it is saved to your profile and grows on its
              own: log a trip and the countries fill themselves in.
            </p>
            <ul className="vmKeepList">
              <li className="vmKeepItem">Saved to your profile, on every device</li>
              <li className="vmKeepItem">Countries added automatically from the trips you log</li>
              <li className="vmKeepItem">See where your friends have been</li>
            </ul>
            <div className="vmKeepButtons" data-track-location="map_builder_keep">
              <a href={WEB_APP_URL} className="vmKeepSave onlyDesktop">
                Create a free account
              </a>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="vmKeepStore onlyTouch">
                <img src="/assets/app_store_badge.svg" alt="Download VOIA on the App Store" />
              </a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="vmKeepStore onlyTouch">
                <img src="/assets/google_play_badge.png" alt="Get VOIA on Google Play" />
              </a>
            </div>
          </div>

          <div className="vmKeepShot">
            <img
              src={screenWorldMap}
              alt="The VOIA app showing visited countries on an interactive globe"
              width="900"
              height="1878"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Answers the questions people search alongside "visited countries map".
          The FAQ schema for this route is built from the same mapFaqs.json by
          scripts/prerender-routes.js, so the two cannot drift. */}
      <section className="vmFaq">
        <div className="vmFaqInner">
          <h2 className="vmFaqTitle">Visited countries map: questions</h2>
          <div className="vmFaqs">
            {MAP_FAQS.map(({ q, a }) => (
              <div key={q} className="vmFaqItem">
                <h3 className="vmFaqQ">{q}</h3>
                <p className="vmFaqA">{a}</p>
              </div>
            ))}
          </div>

          <h2 className="vmFaqTitle vmGuidesTitle">Travel tracking guides</h2>
          <ul className="vmGuides">
            <li>
              <Link to="/blog/how-many-countries-have-you-visited">How to count the countries you have visited</Link>
            </li>
            <li>
              <Link to="/blog/how-to-track-visited-countries">How to track your visited countries</Link>
            </li>
            <li>
              <Link to="/blog/best-apps-to-track-countries-visited">Best apps to track countries visited</Link>
            </li>
          </ul>
        </div>
      </section>

      <Footer variant="lightblue" />
    </div>
  );
};

export default VisitedMapPage;
